import React, { useState, useEffect, useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import { Box, Typography, Container, Paper, alpha, Tabs, Tab } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";

import TabPanel from "../../components/TabPanel";

import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";

import MessagesAPI from "../MessagesAPI";
import Prompts from "../Prompts";
import QueueIntegration from "../QueueIntegration";
import Users from "../Users";
import Queues from "../Queues";
import Connections from "../Connections";
import FileLists from "../Files";

import { 
  SettingsOutlined, 
  Business, 
  ListAlt, 
  HelpOutline, 
  PaletteOutlined, 
  PeopleOutlined, 
  SmartToyOutlined, 
  ExtensionOutlined, 
  CodeOutlined, 
  FolderOpenOutlined 
} from "@mui/icons-material";

import { i18n } from "../../translate/i18n.js";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

import OnlyForSuperUser from "../../components/OnlyForSuperUser";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage/index.js";

const useStyles = makeStyles((theme) => ({
  tabContainer: {
    backgroundColor: "transparent",
    padding: theme.spacing(0, 3), // Alinha com o margin natural do conteúdo logo abaixo
    boxSizing: "border-box",
  },
  tab: {
    fontWeight: "bold",
    textTransform: "none",
  },
  paper: {
    ...theme.scrollbarStyles,
    overflowY: "auto",
    padding: theme.spacing(3),
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
    boxSizing: "border-box", // Adicionado border-box para nãoficar passando do display
    backgroundColor: "transparent",
  },
  container: {
    width: "100%",
    maxHeight: "100%",
  },
  control: {
    padding: theme.spacing(1),
  },
  textfield: {
    width: "100%",
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const themeV4 = useThemeV4();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeV4.palette.type,
          primary: themeV4.palette.primary,
          secondary: themeV4.palette.secondary,
          error: themeV4.palette.error,
          warning: themeV4.palette.warning,
          info: themeV4.palette.info,
          success: themeV4.palette.success,
          text: themeV4.palette.text,
          background: themeV4.palette.background,
          divider: themeV4.palette.divider,
        },
        shape: {
          borderRadius: themeV4.shape.borderRadius,
        },
        typography: {
          fontFamily: themeV4.typography.fontFamily,
        },
      }),
    [themeV4],
  );

  const [tab, setTab] = useState("workspace");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();

  //novo hook
  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    async function findData() {
      setLoading(true);
      try {
        const companyId = user.companyId;
        const company = await find(companyId);

        const settingList = await getAllSettings(companyId);

        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);

        /*  if (Array.isArray(settingList)) {
           const scheduleType = settingList.find(
             (d) => d.key === "scheduleType"
           );
           if (scheduleType) {
             setSchedulesEnabled(scheduleType.value === "company");
           }
         } */
        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const companyId = user.companyId;

    const onSettingsEvent = (data) => {
      if (data.action === "update") {
        setOldSettings((prevState) => {
          if (Array.isArray(prevState)) {
            const aux = [...prevState];
            const settingIndex = aux.findIndex((s) => s.key === data.setting.key);
            if (settingIndex !== -1) {
              aux[settingIndex] = { ...aux[settingIndex], value: data.setting.value };
            } else {
              aux.push(data.setting);
            }
            return aux;
          }
          return prevState;
        });
      }
    };

    const onCompanySettingsEvent = (data) => {
      if (data.action === "update") {
        setSettings((prevState) => {
          const aux = { ...prevState };
          aux[data.column] = data.data;
          return aux;
        });

        if (data.column === "scheduleType") {
          setSchedulesEnabled(data.data === "company");
        }
      }
    };

    socket.on(`company-${companyId}-settings`, onSettingsEvent);
    socket.on(`company-${companyId}-companySettings`, onCompanySettingsEvent);

    return () => {
      socket.off(`company-${companyId}-settings`, onSettingsEvent);
      socket.off(`company-${companyId}-companySettings`, onCompanySettingsEvent);
    };
  }, [socket, user.companyId]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success("Horários atualizados com sucesso.");
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const isSuper = () => {
    return currentUser.super;
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundColor:
            theme.palette.mode === "light" ? "#f5f5f5" : "background.default",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          p: 3,
        }}
      >
        <Container maxWidth="xl" disableGutters>
          {user.profile === "user" ? (
            <ForbiddenPage />
          ) : (
            <>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 4,
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight="800"
                    color="primary"
                    gutterBottom
                  >
                    {i18n.t("settings.title")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Configurações do sistema e personalizações
                  </Typography>
                </Box>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  bgcolor: "transparent",
                }}
              >
                <div className={classes.tabContainer}>
                  <Tabs
                    value={tab}
                    indicatorColor="primary"
                    textColor="primary"
                    scrollButtons="auto"
                    variant="scrollable"
                    onChange={handleTabChange}
                    sx={{
                      bgcolor: "background.paper",
                      borderRadius: "50px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 2,
                      minHeight: "60px",
                      "& .MuiTabs-flexContainer": {
                        justifyContent: "flex-start",
                        gap: "8px",
                        px: 1,
                        py: 0.5,
                      },
                      "& .MuiTab-root": {
                        fontWeight: "600",
                        textTransform: "none",
                        minHeight: "48px",
                        px: 3,
                        borderRadius: "50px",
                        color: "text.secondary",
                        transition: "all 0.2s ease",
                        display: "flex",
                        flexDirection: "row",
                        gap: "8px",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.text.primary, 0.05),
                          color: "primary.main",
                        },
                      },
                      "& .Mui-selected": {
                        color: "primary.main !important",
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      },
                      "& .MuiTabs-indicator": {
                        display: "none",
                      },
                    }}
                  >
                    <Tab
                      className={classes.tab}
                      icon={<SettingsOutlined sx={{ fontSize: 20 }} />}
                      iconPosition="start"
                      label="Workspace"
                      value={"workspace"}
                    />
                    {isSuper() ? (
                      <Tab
                        className={classes.tab}
                        icon={<Business sx={{ fontSize: 20 }} />}
                        iconPosition="start"
                        label="Empresas"
                        value={"companies"}
                      />
                    ) : null}
                    {isSuper() ? (
                      <Tab
                        className={classes.tab}
                        icon={<ListAlt sx={{ fontSize: 20 }} />}
                        iconPosition="start"
                        label={i18n.t("settings.tabs.plans")}
                        value={"plans"}
                      />
                    ) : null}
                    {isSuper() ? (
                      <Tab
                        className={classes.tab}
                        icon={<HelpOutline sx={{ fontSize: 20 }} />}
                        iconPosition="start"
                        label={i18n.t("settings.tabs.helps")}
                        value={"helps"}
                      />
                    ) : null}
                    {isSuper() ? (
                      <Tab
                        className={classes.tab}
                        icon={<PaletteOutlined sx={{ fontSize: 20 }} />}
                        iconPosition="start"
                        label="Whitelabel"
                        value={"whitelabel"}
                      />
                    ) : null}
                    <Tab
                      className={classes.tab}
                      icon={<PeopleOutlined sx={{ fontSize: 20 }} />}
                      iconPosition="start"
                      label="Minha Equipe"
                      value={"users"}
                    />
                    <Tab
                      className={classes.tab}
                      icon={<SmartToyOutlined sx={{ fontSize: 20 }} />}
                      iconPosition="start"
                      label="IA e Atendimento"
                      value={"ia-attendance"}
                    />
                    <Tab
                      className={classes.tab}
                      icon={<ExtensionOutlined sx={{ fontSize: 20 }} />}
                      iconPosition="start"
                      label="Integrações"
                      value={"integrations"}
                    />
                    <Tab
                      className={classes.tab}
                      icon={<CodeOutlined sx={{ fontSize: 20 }} />}
                      iconPosition="start"
                      label="API"
                      value={"api"}
                    />
                    <Tab
                      className={classes.tab}
                      icon={<FolderOpenOutlined sx={{ fontSize: 20 }} />}
                      iconPosition="start"
                      label="Arquivos"
                      value={"files"}
                    />
                  </Tabs>
                </div>
                <div className={classes.paper}>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"workspace"}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3}}>
                      <Options
                        settings={settings}
                        oldSettings={oldSettings}
                        user={currentUser}
                        scheduleTypeChanged={(value) =>
                          setSchedulesEnabled(value === "company")
                        }
                      />
                      {schedulesEnabled && (
                        <SchedulesForm
                          loading={loading}
                          onSubmit={handleSubmitSchedules}
                          initialValues={schedules}
                        />
                      )}
                    </Box>
                  </TabPanel>
                  <OnlyForSuperUser
                    user={currentUser}
                    yes={() => (
                      <>
                        <TabPanel
                          className={classes.container}
                          value={tab}
                          name={"companies"}
                        >
                          <CompaniesManager />
                        </TabPanel>

                        <TabPanel
                          className={classes.container}
                          value={tab}
                          name={"plans"}
                        >
                          <PlansManager />
                        </TabPanel>

                        <TabPanel
                          className={classes.container}
                          value={tab}
                          name={"helps"}
                        >
                          <HelpsManager />
                        </TabPanel>
                        <TabPanel
                          className={classes.container}
                          value={tab}
                          name={"whitelabel"}
                        >
                          <Whitelabel settings={oldSettings} />
                        </TabPanel>
                      </>
                    )}
                  />

                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"users"}
                  >
                    <Users />
                  </TabPanel>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"ia-attendance"}
                  >
                   <Box sx={{ display: "flex", flexDirection: "column", gap: 3}}>
                    <Queues />
                    <Prompts />
                   </Box>
                  </TabPanel>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"integrations"}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3}}>
                      <Connections />
                      <QueueIntegration />
                    </Box>
                  </TabPanel>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"api"}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3}}>
                      <MessagesAPI />
                    </Box>
                  </TabPanel>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"files"}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3}}>
                      <FileLists />
                    </Box>
                  </TabPanel>
                </div>
              </Paper>
            </>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default SettingsCustom;
